import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

type Status = "operational" | "degraded" | "outage" | "unknown";
type Check = { ok: boolean; ms: number; note?: string };
type StatusResponse = {
  status: Status;
  checked_at: string;
  checks: Record<string, Check>;
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

interface Props {
  lang?: "en" | "bn";
  variant?: "footer" | "header";
}

/* Rolling 24h uptime observed by this browser. Each poll is recorded as a
   healthy/unhealthy sample so the widget reports measured availability
   instead of a hardcoded figure. */
const UPTIME_KEY = "medidesk:status-samples";
const WINDOW_MS = 24 * 60 * 60 * 1000;
type Sample = { t: number; ok: boolean };

function loadSamples(): Sample[] {
  try {
    const raw = localStorage.getItem(UPTIME_KEY);
    const parsed = raw ? (JSON.parse(raw) as Sample[]) : [];
    const cutoff = Date.now() - WINDOW_MS;
    return Array.isArray(parsed) ? parsed.filter((s) => s && s.t > cutoff) : [];
  } catch {
    return [];
  }
}

function recordSample(ok: boolean): Sample[] {
  const next = [...loadSamples(), { t: Date.now(), ok }].slice(-1500);
  try {
    localStorage.setItem(UPTIME_KEY, JSON.stringify(next));
  } catch {
    /* storage full or blocked — uptime just won't persist */
  }
  return next;
}

export default function StatusWidget({ lang = "en", variant = "footer" }: Props) {
  const [data, setData] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [samples, setSamples] = useState<Sample[]>(() => loadSamples());

  const t = (en: string, bn: string) => (lang === "bn" ? bn : en);

  const fetchStatus = async () => {
    if (!SUPABASE_URL) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/status`, {
        headers: ANON ? { apikey: ANON, Authorization: `Bearer ${ANON}` } : {},
      });
      const json = (await res.json()) as StatusResponse;
      setData(json);
      setSamples(recordSample(json.status === "operational"));
    } catch {
      setData({ status: "unknown" as Status, checked_at: new Date().toISOString(), checks: {} });
      setSamples(recordSample(false));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uptimePct = samples.length
    ? (samples.filter((s) => s.ok).length / samples.length) * 100
    : null;


  const status: Status = loading ? "unknown" : data?.status ?? "unknown";
  const meta = {
    operational: {
      dot: "bg-emerald-500",
      label: t("All systems operational", "সব সিস্টেম চলছে"),
      Icon: CheckCircle2,
      color: "text-emerald-600",
    },
    degraded: {
      dot: "bg-amber-500 animate-pulse",
      label: t("Partial degradation", "আংশিক বিঘ্ন"),
      Icon: AlertTriangle,
      color: "text-amber-600",
    },
    outage: {
      dot: "bg-rose-500 animate-pulse",
      label: t("Service disruption", "সেবা বিঘ্নিত"),
      Icon: XCircle,
      color: "text-rose-600",
    },
    unknown: {
      dot: "bg-slate-400",
      label: t("Checking status…", "স্ট্যাটাস দেখা হচ্ছে…"),
      Icon: Loader2,
      color: "text-slate-500",
    },
  }[status];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-2 rounded-full border border-[#e2e8f0] bg-white px-3 py-1.5 text-xs font-medium text-[#334155] hover:bg-[#f8fafc] transition-colors ${
            variant === "header" ? "shadow-sm" : ""
          }`}
          aria-label={t("View system status", "সিস্টেম স্ট্যাটাস দেখুন")}
        >
          <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
          <span>{meta.label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4 rounded-2xl" align="end">
        <div className="flex items-start gap-2 mb-3">
          <meta.Icon className={`w-4 h-4 mt-0.5 ${meta.color} ${loading ? "animate-spin" : ""}`} />
          <div>
            <div className="text-sm font-semibold text-[#0f172a]">{meta.label}</div>
            {data?.checked_at && (
              <div className="text-[11px] text-[#64748b]">
                {t("Last checked", "সর্বশেষ চেক")}: {new Date(data.checked_at).toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>

        {uptimePct !== null && (
          <div className="mb-3 rounded-xl bg-[#f8fafc] px-3 py-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#334155]">{t("Uptime (24h)", "আপটাইম (২৪ ঘণ্টা)")}</span>
              <span className="font-semibold text-[#0f172a]">{uptimePct.toFixed(2)}%</span>
            </div>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[#e2e8f0]">
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${Math.max(2, uptimePct)}%` }}
              />
            </div>
            <div className="mt-1 text-[10px] text-[#94a3b8]">
              {t(
                `Measured from ${samples.length} check${samples.length === 1 ? "" : "s"} on this device`,
                `এই ডিভাইসে ${samples.length}টি চেক থেকে হিসাব`,
              )}
            </div>
          </div>
        )}



        {data?.checks && Object.keys(data.checks).length > 0 && (
          <ul className="space-y-1.5 mb-3">
            {Object.entries(data.checks).map(([name, c]) => (
              <li key={name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-[#334155]">
                  <span className={`w-1.5 h-1.5 rounded-full ${c.ok ? "bg-emerald-500" : "bg-rose-500"}`} />
                  <span className="capitalize">{name}</span>
                </span>
                <span className="text-[#64748b]">
                  {c.ok ? `${c.ms} ms` : c.note ?? t("down", "বন্ধ")}
                </span>
              </li>
            ))}
          </ul>
        )}

        <div className="border-t border-[#e2e8f0] pt-3 text-[11px] leading-relaxed text-[#475569]">
          <div className="font-semibold text-[#0f172a] mb-1">
            {t("How we communicate incidents", "ইনসিডেন্ট কীভাবে জানানো হয়")}
          </div>
          <p>
            {t(
              "This widget polls a public status endpoint every 60 seconds. During an incident we post updates here, in the app banner, and by email to clinic admins. Post-incident notes are added to the Security page within 5 business days.",
              "এই উইজেট প্রতি ৬০ সেকেন্ডে পাবলিক স্ট্যাটাস এন্ডপয়েন্ট চেক করে। ইনসিডেন্টের সময় আপডেট এখানে, অ্যাপ ব্যানারে এবং ক্লিনিক অ্যাডমিনদের ইমেইলে দেওয়া হয়। ৫ কর্মদিবসের মধ্যে সিকিউরিটি পেজে পোস্ট-ইনসিডেন্ট নোট যোগ করা হয়।",
            )}
          </p>
          <div className="mt-2 flex gap-3">
            <a href="/security" className="text-[#0f8f6b] font-semibold hover:underline">
              {t("Security page", "সিকিউরিটি পেজ")}
            </a>
            <a
              href="mailto:support@doctorsx.app?subject=Incident%20report"
              className="text-[#0f8f6b] font-semibold hover:underline"
            >
              {t("Report an issue", "সমস্যা জানান")}
            </a>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
